import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Stack,
  alpha,
  Divider,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { getConsultantsV2 } from '../../../helpers/api';
import { openNotification } from '../../../helpers/notification';
import { Empty } from 'antd';
import MUIDataTable from 'mui-datatables';
import {
  ApprovalOutlined,
  Close,
  DeleteOutline,
  KeyboardArrowRight,
  MoreHoriz,
  Search,
  PersonOutline,
  MailOutline,
  PhoneOutlined,
  BadgeOutlined,
} from '@mui/icons-material';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ApproveNewConsultant from './consultants-dialogs/ApproveNewConsultant';
import { useHistory } from 'react-router-dom';
import LoaderSkeleton from '../../common/LoaderSkeleton';

const ConsultantScreen = withRouter(({ props }) => {
  const history = useHistory();
  const [consultantList, setConsultantList] = useState([]);
  const [consultantSelected, setConsultantSelected] = useState();
  const [consultantLoading, setConsultantLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParam, setSearchParam] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [pageNum, setPageNum] = useState(0);
  const pageSize = 10;
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [dialogApprove, setDialogApprove] = useState(false);

  useEffect(() => {
    fetchConsultantList();
  }, []);

  useEffect(() => {
    if (refresh) {
      fetchConsultantList();
    }
  }, [refresh]);

  const handleMenuOpen = (event) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const fetchConsultantList = async () => {
    setConsultantLoading(true);
    try {
      const res = await getConsultantsV2(searchParam, pageNum, pageSize);
      const { totalCount, data, status } = res?.result;
      if (status?.code === '00') {
        setConsultantList(data);
        setTotalCount(totalCount);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Something went wrong. Please try again' });
    } finally {
      setConsultantLoading(false);
      setRefresh(false);
    }
  };

  const renderContent = () => {
    const columns = [
      {
        name: 'firstname',
        label: 'Consultant Name',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  bgcolor: alpha('#1976d2', 0.1),
                  color: 'primary.main',
                  display: 'flex',
                }}
              >
                <PersonOutline sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {consultantList[index]?.firstname} {consultantList[index]?.lastname}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {consultantList[index]?.role}
                </Typography>
              </Box>
            </Stack>
          ),
        },
      },
      {
        name: 'email',
        label: 'Contact Info',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <MailOutline sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="body2">{consultantList[index]?.email}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PhoneOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="textSecondary">
                  {consultantList[index]?.phone}
                </Typography>
              </Stack>
            </Box>
          ),
        },
      },
      {
        name: 'status',
        label: 'Status',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const status = consultantList[index]?.status;
            let themeColor = '#1976d2';
            if (status === 'ACTIVE') themeColor = '#2e7d32';
            if (status === 'PENDING') themeColor = '#ed6c02';
            if (status === 'INACTIVE' || status === 'DEACTIVATED') themeColor = '#d32f2f';

            return (
              <Chip
                label={status}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  bgcolor: alpha(themeColor, 0.1),
                  color: themeColor,
                  borderRadius: '6px',
                }}
              />
            );
          },
        },
      },
      {
        name: '',
        label: 'Actions',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
              align="center"
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Box align="center">
              <Button
                variant="text"
                size="small"
                endIcon={<KeyboardArrowRight />}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
                onClick={() => history.push(`consultants/${consultantList[index]?.userId}/view`)}
              >
                View
              </Button>
              {/* <IconButton
                size="small"
                onClick={(e) => {
                  setConsultantSelected(consultantList[index]);
                  handleMenuOpen(e);
                }}
              >
                <MoreHoriz />
              </IconButton> */}
            </Box>
          ),
        },
      },
    ];

    return (
      <MUIDataTable
        data={consultantList}
        columns={columns}
        options={{
          elevation: 0,
          selectableRows: 'none',
          responsive: 'standard',
          search: false,
          download: false,
          print: false,
          filter: false,
          viewColumns: false,
          pagination: false,
          setRowProps: (ev, dataIndex) => ({
            style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
          }),
          setTableProps: () => ({ size: 'small' }),
        }}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F4F6F8', minHeight: '100vh', mt: -1 }}>
      {/* HEADER SECTION */}
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Consultant Registry
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor, approve and manage registered consultants.
            </Typography>
          </Box>

          <Box sx={{ width: { xs: '100%', sm: 300 } }}>
            <TextField
              fullWidth
              value={searchParam}
              placeholder="Search by name or email..."
              size="small"
              onChange={(e) => setSearchParam(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setRefresh(true)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchParam && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchParam('');
                        setRefresh(true);
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: '12px', bgcolor: '#f9fafb' },
              }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* MAIN DATA TABLE AREA */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {consultantLoading ? (
          <Box sx={{ p: 4 }}>
            <LoaderSkeleton />
            <Divider sx={{ my: 3 }} />
            <LoaderSkeleton />
          </Box>
        ) : consultantList?.length < 1 ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Empty description="No consultants found" />
          </Box>
        ) : (
          renderContent()
        )}
      </Paper>

      {/* OVERFLOW MENU */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            width: 200,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <MenuItem
          sx={{ gap: 1.5 }}
          onClick={() => {
            history.push(`consultants/${consultantSelected?.userId}/view`);
            handleMenuClose();
          }}
        >
          <InfoCircleOutlined style={{ fontSize: '16px' }} />
          <Typography variant="body2" fontWeight={600}>
            View Profile
          </Typography>
        </MenuItem>

        {consultantSelected?.status === 'PENDING' && (
          <MenuItem
            sx={{ gap: 1.5 }}
            onClick={() => {
              setDialogApprove(true);
              handleMenuClose();
            }}
          >
            <ApprovalOutlined fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={600} color="primary">
              Approve Access
            </Typography>
          </MenuItem>
        )}

        <Divider />

        <MenuItem sx={{ gap: 1.5 }}>
          <EditOutlined style={{ fontSize: '16px' }} />
          <Typography variant="body2" fontWeight={600}>
            Edit Record
          </Typography>
        </MenuItem>

        <MenuItem sx={{ gap: 1.5 }}>
          <DeleteOutline fontSize="small" color="error" />
          <Typography variant="body2" fontWeight={600} color="error">
            Deactivate
          </Typography>
        </MenuItem>
      </Menu>

      {/* DIALOGS */}
      {dialogApprove && (
        <ApproveNewConsultant
          open={dialogApprove}
          onClose={() => {
            setDialogApprove(false);
            setRefresh(true);
          }}
          consultant={consultantSelected}
        />
      )}
    </Box>
  );
});

export default ConsultantScreen;
